<template>
  <div class="oring-assistant__container">
    <div class="oring-assistant__container__cards">
      <div
        v-for="(card, idx) in cards"
        :key="idx"
        class="card"
      >
        <div class="card-content">
          <div class="card-content__title">
            <h3 class="h4 card-content__title-text">
              {{ card.title }}
            </h3>
            <span
              class="card-content__title-icon"
              v-show="!hideInfoIcon"
            >
              <component
                :is="infoIconComponent"
                :class="['icon', 'icon--svg']"
                role="button"
                tabindex="0"
                aria-label="Info"
                @click="card.popupContent ? openPopup(idx) : null"
                @keydown.enter="card.popupContent ? openPopup(idx) : null"
              />
            </span>
            <PopupBase
              v-if="card.popupContent"
              :show="popupIdx === idx"
              @close-pop-up="closePopup"
              :size="popupSizeSmall"
            >
              <PopupContent
                :title="card.popupContent.title"
                :content="card.popupContent.content"
              />
            </PopupBase>
          </div>
          <div
            class="card-content__description"
            v-if="card.description"
          >
            {{ card.description }}
          </div>
          <div class="card-content__container">
            <RadioGroup v-model="card.model.value">
              <RadioButtonCentered
                v-for="option in card.options"
                :key="String(option.value)"
                :value="option.value"
                :label="option.label"
                :img-url="option.imgUrl"
                :disabled="option.disabled === true"
                :class="{ 'option--disabled': option.disabled === true }"
              />
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue'
import RadioGroup from '~/components/input-components/RadioGroup.vue'
import RadioButtonCentered from '~/components/input-components/RadioButtonCentered.vue'
import ActionInfoFilledIcon from '~/assets/icons/action_info_filled.svg?component'
import PopupBase from '~/components/popup/PopupBase.vue'
import PopupContent from './PopupContent.vue'
import {POPUP} from '~/assets/js/constants'

interface CardConfig {
  title: string;
  description: string | null;
  model: any;
  options: Array<{ value: string | boolean; label: string; imgUrl: string ;  disabled?: boolean;}>;
  popupContent?: string | { title?: string; content?: string } | null | (() => any);
}

const props = withDefaults(defineProps<{
  cards: CardConfig[];
  infoIconComponent?: any;
  hideInfoIcon?: boolean;
}>(), {
  hideInfoIcon: false,
  infoIconComponent: undefined
})
const infoIconComponent = computed(() => props.infoIconComponent || ActionInfoFilledIcon)
const popupSizeSmall = POPUP.Size.small
const popupIdx = ref(-1)
const openPopup = (idx: number) => {
  popupIdx.value = idx
}
const closePopup = () => {
  popupIdx.value = -1
}
</script>

<style lang="scss">
.oring-assistant__container {
  width: 100%;
  margin-bottom: 1rem;

  &__cards {
    display: flex;
    width: 100%;

    .card:first-child {
      margin-left: 0;
    }

    .card:last-child {
      margin-right: 0;
    }

    .card {
      width: 50%;
      padding: 1rem;
      box-shadow: $shadow-default;
      margin: 1.25rem .625rem 0;

      h3 {
        margin-top: 0;
        margin-bottom: 0;
        font-weight: $base-text-font-weight-semi-bold;
        line-height: 1.5rem;
      }

      .card-content__title {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding-bottom: 1rem;

        .card-content__title-text {
          margin: 0;
          font-weight: $base-text-font-weight-semi-bold;
          line-height: 1.5rem;
        }

        .card-content__title-icon {
          display: flex;
          align-items: center;

          .icon.icon--svg {
            color: $color__primary;
            height: 1.5rem;
            width: 1.5rem;
            cursor: pointer;
          }
        }
      }

      .card-content__description {
       padding-bottom: 1rem;
       font-weight: $base-text-font-weight-light;
      }

      .card-content__container {
        display: flex;
        flex-direction: column;
        max-width: 20.8rem;

        .inputs-radio-group {
          gap: 1rem;

          .inputs-radio-button-centered {
            height: 3.875rem;
          }
        }
      }
    }

    .card:only-child {
      width: 100%;
    }

    @include breakpoint-down($md) {
      flex-direction: column;

      .card {
        width: calc(100% - 2rem);
        margin-left: 0;
        margin-right: 2rem;
      }

      .card:only-child {
        width: auto;
      }
    }
  }

  .inputs-radio-button-centered.option--disabled {
    opacity: 0.5;
    filter: grayscale(0.7);
    pointer-events: none;
  }
}
</style>
